import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  downloadVaccination,
  getMyVaccination,
  getMyVaccinationDetails,
} from "service";
import { toast } from "sonner";
import { useParams } from "react-router-dom";
import { IMyShotDetails, IMyVaccinationResponse } from "types";
import { useState } from "react";
import axios from "axios";

export const useMyVaccinations = () => {
  const params = useParams();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState<{
    current: number;
    pageSize: number;
  }>({
    current: 1,
    pageSize: 10,
  });

  const {
    data: myVaccinations,
    isLoading: myVaccinationsLoading,
    isError: myVaccinationsError,
    refetch,
    isPlaceholderData,
  } = useQuery<IMyVaccinationResponse>({
    queryKey: ["my-vaccinations", pagination.current, pagination.pageSize],

    queryFn: async () => {
      const response = await getMyVaccination(
        pagination.current,
        pagination.pageSize
      );
      return response;
    },
    staleTime: 0,
  });

  const handlePageChange = (page: number, pageSize?: number) => {
    const newPage = Math.max(1, page);
    setPagination((prev) => ({
      current: newPage,
      pageSize: pageSize ?? prev.pageSize,
    }));
    setTimeout(() => refetch(), 0);
  };

  const someMyVaccination = {
    count: myVaccinations?.count || 0,
    next: myVaccinations?.next || "",
    previous: myVaccinations?.previous || null,
    results: myVaccinations?.results.slice(0, 5) || [],
  };

  const { data: myVaccinationsDetails, isLoading: myShotDetailsLoading } =
    useQuery<IMyShotDetails>({
      queryKey: ["my-vaccinations-details", params.id],
      queryFn: async () => {
        const response = await getMyVaccinationDetails(params.id!);
        return response;
      },
      enabled: !!params.id,
    });

  // download vaccination
  const { mutate: downloadVaccine, isPending: downloadLoading } = useMutation({
    mutationFn: async (v_id: string) => {
      const response = await downloadVaccination(v_id);
      return response;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: ["wallet"],
      });
      toast.success("Download link sent to your email");
    },
    onError: (error: any) => {
      if (axios.isAxiosError(error)) {
        toast.error(error.response?.data.detail);
      }
      toast.error("Sorry, an error occurred.");
    },
  });

  const { mutate: downloadAllVaccine, isPending: downloadAllLoading } =
    useMutation({
      mutationFn: async () => {
        const response = await downloadVaccination("", true);
        return response;
      },
      onSuccess: (data) => {
        toast.success("Download link sent to your email");
        queryClient.invalidateQueries({
          queryKey: ["wallet"],
        });
      },
    });

  return {
    myVaccinations,
    isLoading: myVaccinationsLoading || myShotDetailsLoading,
    isError: myVaccinationsError,
    myVaccinationsDetails,
    handlePageChange,
    pagination,
    isPlaceholderData,
    downloadVaccine,
    downloadAllVaccine,
    downloadLoading,
    someMyVaccination,
  };
};
